import React, { Dispatch, SetStateAction, useContext, useMemo } from 'react';
import Image from 'next/image';
import ProductData from '@customTypes/product';
import addButton from '@assets/images/add.svg';
import { store } from '@context/store';
import toggleErrorPopup from '@helpers/toggleErrorPopup';
import {
  AMPLITUDE_EXPERIMENT_VARIANT_NAME,
  AMPLITUDE_EXPERIMENT_VARIANT_NAME_2,
} from '@constants/amplitudeExperiments';
import { ExperimentTypeABC } from '@customTypes/context';

type Props = {
  setParentCounter?: Dispatch<SetStateAction<number>>;
  productData: ProductData;
  personType: string;
  adultCounter: number;
  childrenCounter: number;
  counter: number;
  date: Date | undefined;
  scrollToDateField: () => void;
  setIsBottomDrawerOpen?: Dispatch<SetStateAction<boolean>>;
  availableUnits?: number;
  ctasUpdateExperiment?: ExperimentTypeABC;
};

const defaultProps = {
  setParentCounter: null,
  setIsBottomDrawerOpen: false,
  availableUnits: null,
};

export default function Counter({
  setParentCounter,
  productData,
  personType,
  adultCounter,
  childrenCounter,
  counter,
  date,
  scrollToDateField,
  setIsBottomDrawerOpen,
  availableUnits,
  ctasUpdateExperiment = 'control',
}: Props) {
  const globalState = useContext(store);
  const { dispatch } = globalState;

  const getProductNotAllowedMessage = (productName: string, max: number) => {
    const name = productName && productName.toLowerCase();
    return `This ${name} does not allow more than ${max ?? 0} ${personType}`;
  };

  const getCabanaMaxOccupancyMessage = (productName: string, maxPersonOccupancy: number) => {
    const name = productName && productName.toLowerCase();
    return `Sorry, this ${name} has a maximum occupancy of ${maxPersonOccupancy ?? 0}`;
  };

  const checkValidity = (value: number) => {
    let message;
    if (date) {
      if (productData.individual_billing) {
        const maxLimit =
          personType === 'adults'
            ? availableUnits || productData.adult_available_unit
            : adultCounter * productData.max_children_per_adult;

        if (value < maxLimit) {
          const newCounter = counter + 1;
          if (setParentCounter) setParentCounter(newCounter);
        } else {
          message = getProductNotAllowedMessage(productData.name, maxLimit);
          toggleErrorPopup(message, dispatch);
        }
      } else if (!productData.individual_billing) {
        const maxLimit =
          personType === 'adults'
            ? productData.max_adults_occupancy
            : productData.max_children_occupancy;
        if (adultCounter + childrenCounter < productData.max_person_occupancy) {
          if (value < maxLimit) {
            const newCounter = counter + 1;
            if (setParentCounter) setParentCounter(newCounter);
          } else {
            message = getProductNotAllowedMessage(productData.name, maxLimit);
            toggleErrorPopup(message, dispatch);
          }
        } else {
          message = getCabanaMaxOccupancyMessage(
            productData.name,
            productData.max_person_occupancy,
          );
          toggleErrorPopup(message, dispatch);
        }
      }
    } else {
      if (setIsBottomDrawerOpen) setIsBottomDrawerOpen(false);
      scrollToDateField();
    }
  };

  const decreaseCounter = () => {
    let newCounter;
    if (personType === 'adults') {
      newCounter = counter > 1 ? counter - 1 : 1;
    } else {
      newCounter = counter > 0 ? counter - 1 : 0;
    }

    if (setParentCounter) setParentCounter(newCounter);
  };

  const increaseCounter = () => {
    checkValidity(counter);
  };

  const buttonBgColor = useMemo(() => {
    if (ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME) {
      return 'bg-solar-button-primary-default hover:bg-solar-button-primary-hover active:bg-solar-button-primary-pressed';
    }

    if (ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME_2) {
      return 'bg-solar-button-ultramarine-default hover:bg-solar-button-ultramarine-hover active:bg-solar-button-ultramarine-pressed';
    }

    return 'bg-rp-primary active:bg-rp-primary-dark';
  }, [ctasUpdateExperiment]);

  return (
    <div className="flex w-32 h-10 border border-solid rounded-full items-center px-1 justify-between font-rp-pn-regular bg-white shadow-lg">
      <button
        type="button"
        className="rounded-full h-8 w-8 border border-solid flex justify-center items-center text-3xl cursor-pointer active:bg-rp-light-gray"
        onClick={decreaseCounter}
        aria-label={`Decrease ${personType}`}
      >
        <div className="w-3 h-0.5 bg-rp-primary-black" />
      </button>

      <div className="w-12 h-full border-l border-r border-solid flex justify-center items-center text-base">
        {counter}
      </div>

      <button
        type="button"
        className={`rounded-full h-8 w-8 border border-solid flex justify-center items-center text-3xl cursor-pointer ${buttonBgColor}`}
        onClick={increaseCounter}
        aria-label={`Increase ${personType}`}
      >
        <Image src={addButton} alt="add-button" />
      </button>
    </div>
  );
}

Counter.defaultProps = defaultProps;
